import React, { useMemo, useState, useEffect } from "react"
import Label, { useLabels } from "components/general/Label";
import { useViewport } from 'components/general/useViewPort'
import MirsTable from "components/general/table/Table"
import { Accordion } from 'react-bootstrap'
import { useUrlFriendlyToParameters, useNavigateTo } from 'helpers/urlFriendly';
import Noc from "components/presentation/Noc";
import { getDateTimeFormat, getEntityLessThanTen, getHeight } from 'helpers/utils'
import { Stack } from 'react-bootstrap'
import Competitor from 'components/presentation/Competitor';
import { GGetSportsCode, getSportCodeEntityObjects } from "../SportCode";
import DateTime from "components/presentation/DateTime";
import { ColumnExpander } from "../TableColumns";
import { TeamEntriesExpanded } from "./TeamEntriesExpanded";
import { useQueryClient } from "@tanstack/react-query";
import { usePositions } from "../Positions";

const getConfiguration = (breakPoint, sportEntryCodes, sportEntryACodes) => {
  const isMobile = (breakPoint === "sm" || breakPoint === "xs")
  let configuration = {
    columns: [],
    sortBy: [],
    disableSort: false,
  };
  // configuration.sortBy = isMobile ? [{ id: "NOCAndName", desc: false }] : [{ id: 'Competitor', desc: false }];
  configuration.sortBy = isMobile ? [{ id: "Number", desc: false }] : [{ id: 'Competitor', desc: false }];
  configuration.columns = isMobile ? TableColumnsSM(sportEntryCodes, sportEntryACodes) : TableColumns(sportEntryCodes, sportEntryACodes)
  return configuration;
};

function extractDataFromTeam(team) {
  // as team has only one event in registered events
  // find person registeredEvent that has the same code as team event code
  // return person registered events as array containing that mutual event only
  const athletes = [];
  team?.athletes?.forEach((ath) => {
    const registeredEventOfPersoAndTeam = ath?.person?.registeredEvents?.find(re => re?.code === team?.registeredEvents?.at(0)?.code)
    if (registeredEventOfPersoAndTeam) {
      athletes.push({ ...ath, person: { ...ath?.person, registeredEvents: [registeredEventOfPersoAndTeam] } })
    }
  })
  let orderCounter = 100;
  team?.teamCoaches?.sort((a, b) => {
    const coachName = a?.coach?.name?.toLowerCase().localeCompare(b?.coach?.name?.toLowerCase());
    const coachOrder = a?.order - b?.order;
    // const coachFunctionOrder = a?.function?.fOrder - b?.function?.fOrder;
    // return coachFunctionOrder || coachName;
    return coachOrder || coachName;
  });

  team?.teamCoaches?.forEach((coachItem) => {
    athletes.push({
      order: orderCounter + coachItem.order,
      person: {
        ...coachItem?.coach,
        mainFunction: { ...coachItem?.function }
      }
    });
  });
  return athletes
}

const SportEntriesTeamsTable = ({ data }) => {
  const { breakPoint } = useViewport();
  const { mirs_discipline, mirs_competition, mirs_language, mirs_event } = useUrlFriendlyToParameters()
  const [paramsUrl, setParamsUrl] = useState({});
  const [sportEntryCodes, setSportEntryCodes] = useState([])
  const [sportEntryACodes, setSportEntryACodes] = useState([])
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const fLabel = useLabels();
  const queryClient = useQueryClient();
  useNavigateTo(paramsUrl, shouldNavigate, setShouldNavigate, 'results')

  useEffect(() => {
    const dataSportCodes = queryClient.getQueryData(["SportCodes", mirs_competition, mirs_language])?.sportCodes;
    const entryCodes = getSportCodeEntityObjects(dataSportCodes, mirs_discipline, '@Entry');
    const entryACodes = getSportCodeEntityObjects(dataSportCodes, mirs_discipline, '@EntryA');
    setSportEntryCodes(entryCodes)
    setSportEntryACodes(entryACodes)
  }, [mirs_competition, mirs_discipline, mirs_language, queryClient])

  const config = useMemo(() => getConfiguration(breakPoint, sportEntryCodes, sportEntryACodes), [breakPoint, sportEntryCodes, sportEntryACodes]);

  const renderRowSubComponent = React.useCallback(({ row }) => {
    let componentExpanded = '';
    componentExpanded = < TeamEntriesExpanded row={row?.original} sportEntryCodes={sportEntryCodes} event={mirs_event} config={config.configs?.columnsAdded} />
    return componentExpanded;
  }, [config.configs?.columnsAdded, sportEntryCodes, mirs_event]);

  //Prepare URL to navigate
  const isMobile = (breakPoint === "sm" || breakPoint === "xs");
  return (
    <>
      <div className="d-flex m-2">
        <b className="w-50 p-0"><Label data={"HeaderTeamName"} /></b>
        <b className="w-50 p-0"><Label data={"HeaderEvent"} /></b>
      </div>
      <Accordion className='schedule-container' /*defaultActiveKey={0}*/>

        {data?.map((team, i) => {
          return <Accordion.Item eventKey={i} key={i}>
            <Accordion.Header className='schedule-title'>
              <div className="w-50 d-flex">
                <Noc {...team.organisation} showImage={true} showLongDesc={false} hideNocCode={isMobile ? true : false} />
                {team.organisation.description || team.organisation.longDescription}
              </div>
              <div>{team?.registeredEvents?.at(0)?.event?.description}</div>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <MirsTable
                className=""
                data={extractDataFromTeam(team)}
                columns={config.columns}
                keyRow='eventUnit-code'
                initialSortBy={config.sortBy}
                id={`mirs-table-teams`}
                summaryDesc={fLabel({ data: 'SummaryEntriesPage' })}
                renderRowSubComponent={renderRowSubComponent}
              />
            </Accordion.Body>
          </Accordion.Item>
        })}
      </Accordion>
    </>
  )
}

export default SportEntriesTeamsTable;

function getPlayerDataFromCellProps(cellProp) {
  return cellProp.cell?.row?.original?.person;
}

function TableColumns(sportEntryCodes, sportEntryACodes) {

  let cols = [
    ColumnNumber({}),
    ColumnCompetitor({}),
    ColumnDOB({}),
  ]

  sportEntryACodes.forEach(e => {
    //e.sportCode === 'HEIGHT' ? cols.push(ColumnHeight({})) : cols.push(ColumnEntryA(e))
    if (e.sportCode === 'HEIGHT') {
      cols.push(ColumnHeight({}))
    } else if (e.sportCode !== 'BIB') {
      cols.push(ColumnEntryA(e))
    }
  })

  sportEntryCodes.forEach(e => {
    if (e.order < 10) {
      if (e.sportCode === "POSITION") {
        cols.push(ColumnPosition(e))
      } else {
        cols.push(ColumnEntry(e))
      }
    }
  });

  let filteredSportEntryCodes = getEntityLessThanTen(sportEntryCodes);
  cols.push(ColumnExpander({ type: 'TeamAlwaysTrue', idNode: "teamEntry", sportEntryCodes: filteredSportEntryCodes }))

  return (
    cols
  )
}

function TableColumnsSM(sportEntryCodes, sportEntryACodes) {
  let cols = [
    ColumnNumber({}),
    ColumnCompetitorWithDOB({})
  ]

  sportEntryACodes.forEach(e => {
    //e.sportCode === 'HEIGHT' ? cols.push(ColumnHeight({})) : cols.push(ColumnEntryA(e))
    if (e.sportCode === 'HEIGHT') {
      cols.push(ColumnHeight({}))
    } else if (e.sportCode !== 'BIB') {
      cols.push(ColumnEntryA(e))
    }
  });

  sportEntryCodes.forEach(e => {
    if (e.order < 10) {
      if (e.sportCode === "POSITION") {
        cols.push(ColumnPosition(e))
      }
    }
  });

  return (
    cols
  )
}

export function ColumnNumber({
  disableSort = false,
  className = 'text-start',
  headerClassName = 'text-start',
  label = "HeaderNumber"
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      // accessor: 'number',
      // accessor: (props) => (props?.person?.registeredEvents?.at(0).bib),
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      id: 'Number',
      Cell: (props) => {
        const player = getPlayerDataFromCellProps(props);
        return (
          <span>{player?.registeredEvents?.[0].bib}</span>
        )
      }
    }
  )
}

export function ColumnDOB({
  disableSort = true,
  className = 'text-center text-wrap',
  headerClassName = 'text-center text-wrap',
  label = 'HeaderBirthDate',
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'DOB',
      accessor: 'birthDate',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      Cell: (props) => {
        const player = getPlayerDataFromCellProps(props);
        const format = getDateTimeFormat('DayMonthShortYear');
        return (player?.mainFunction?.category === 'A') ? (<DateTime datetime={player?.birthDate} format={format} />) : '';
      }
    }
  )
}

export function ColumnHeight({
  disableSort = true,
  className = 'text-center text-wrap',
  headerClassName = 'text-center text-wrap',
  label = 'HeaderHeightMFt',
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'height',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      Cell: (props) => {
        const player = getPlayerDataFromCellProps(props);

        return (getHeight(player?.height))
      }
    }
  )
}
export function ColumnPosition(entry) {
  const positions = usePositions();
  return (
    {
      Header: <Label data="HeaderPosDesc" legendFixed={true} />,
      id: 'position',
      className: 'text-center text-wrap',
      headerClassName: 'text-center text-wrap',
      disableSortBy: true,
      Cell: (props) => {
        const player = getPlayerDataFromCellProps(props);
        let category = player?.mainFunction?.category;
        if (category !== 'A') {
          return player?.mainFunction?.description;
        } else {
          let player = props.cell.row.original
          let entryEvent = player?.person?.registeredEvents.at(0)?.eventEntries?.find(ee => ee?.ee_code === entry?.sportCode)
          //let entryEvent = registeredEvent?.eventEntries?.find(ee => ee?.ee_code === entry?.sportCode);
          // return '(entryEvent?.ee_value)'
          return positions(entryEvent?.ee_value);
          // return registeredEvent?.eventEntries?.find(ee => ee?.ee_code === entry?.sportCode)?.ee_value;
        }
      }
    }
  )
}

export function ColumnCompetitor({
  disableSort = false,
  className = 'text-start',
  headerClassName = 'text-start',
  label = "LabelName"
}) {
  return (
    {
      Header: <Label data={label} legendFixed={true} />,
      id: 'Competitor',
      //accessor: (props) => (props?.person?.name?.toUpperCase()),
      //multiSort: [{ id: 'Competitor' }, { id: 'Number', desc: false }],
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      Cell: (props) => {
        const player = getPlayerDataFromCellProps(props);
        let nationality = player?.nationality?.code;
        let organisation = player?.organisation?.code;
        let showNationality = false;
        if (nationality !== organisation && player?.mainFunction?.category !== 'A') {
          showNationality = true;
        }
        return (
          <div className="d-flex">
            <Competitor {...player} shortName={player?.name} noLink={player?.mainFunction?.category === 'T'} hideNoc={true} key={`participant-${player?.code}`} />
            {showNationality && <Noc {...player?.nationality} />}
          </div>
        )
      }
    }
  )
}


export function ColumnCompetitorWithDOB({
  disableSort = false,
}) {
  return (
    {
      Header: <Stack direction='vertical'>
        <Label data={"LabelName"} />
        <Stack direction="horizontal">
          <Label data={"HeaderBirthDate"} />
          <span> / </span>
          <Label data={"HeaderGender"} />
        </Stack>
      </Stack>,
      accessor: (props) => ((props?.organisation?.code || " ") + (props?.name?.toUpperCase() || "")),
      className: '',
      headerClassName: '',
      disableSortBy: disableSort,
      id: 'CompetitorDOB',
      Cell: (props) => {
        const player = getPlayerDataFromCellProps(props);
        return (
          <Stack direction='vertical'>
            <Competitor {...player} hideNoc={true} hidePicture={false} key={`participant-${player?.code}`} />
            <div>
              <DateTime datetime={player?.birthDate} format={getDateTimeFormat('DayMonthShortYear')} />
              {player?.personGender && <span>{'/' + player?.personGender?.description}</span>}
            </div>
          </Stack>
        )
      }
    }
  )
}

export function ColumnEntryA(entryA) {
  return (
    {
      Header: entryA.description,
      id: entryA.sportCode,
      className: 'text-start',
      headerClassName: 'text-start',
      disableSortBy: false,
      Cell: (props) => {
        let data = props.cell.row.original
        let registeredEvent = data?.person?.registeredEvents?.find(x => x != undefined)
        let entryVal = registeredEvent?.eventEntries?.find(ee => ee.ee_code === entryA.sportCode)
        return entryVal?.ee_value
      }
    }
  )
}

export function ColumnEntry(entry) {
  const { mirs_discipline } = useUrlFriendlyToParameters();
  return (
    {
      Header: entry.description,
      id: entry.sportCode,
      className: 'text-start',
      headerClassName: 'text-start',
      disableSortBy: false,
      Cell: (props) => {
        let data = props.cell.row.original
        let registeredEvent = data?.person?.registeredEvents?.find(x => x != undefined)
        let entryVal = registeredEvent?.eventEntries?.find(ee => ee?.ee_code === entry?.sportCode)
        // return entryVal?.ee_value
        // return entry?.note ? GetSportsCodefromNote(mirs_discipline, entry?.note, entryVal?.ee_value)?.description: entryVal?.ee_value
        return entry?.note ? GGetSportsCode(mirs_discipline, entry?.note, entryVal)?.description : entryVal?.ee_value
      }
    }
  )
}
